import styled from 'styled-components';
import type { CardProps } from 'ui/cardV2/types';
import { FlexSpacer } from 'ui/flexSpacer';
import { ChevronRightNew16 } from 'ui/icons';
import { body2, body2Bold, caption as captionStyles, colors, spacing } from 'ui/theme';

export const ClickableCard = styled.div<CardProps>`
  width: 100%;
  box-sizing: border-box;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.threeQuarter};
  padding: ${spacing.base} ${spacing.plusHalf};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};

  &:last-of-type {
    border-bottom: none;
  }

  &[data-clickable="true"]:hover,
  &[data-clickable="true"]:focus {
    cursor: pointer;
    background-color: ${colors.backgroundPrimaryHover};
  }
`;

export const ResourceName = styled.span`
  ${body2Bold}
  color: ${colors.textLink};
`;

export const AlertText = styled.span`
  ${body2}
  color: ${colors.textSecondary};
`;

const Caption = styled.span`
  ${captionStyles}
  color: ${colors.textSecondary};
`;

type AlertProps = {
  hoverable?: boolean;
  resourceName: React.ReactNode;
  content: React.ReactNode;
  caption: React.ReactNode;
  onClick: () => void;
};

export const RecentCampaignAlert = ({
  resourceName,
  content,
  caption,
  hoverable = true,
  onClick,
  ...rest
}: AlertProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onClick();
    }
  };

  return (
    <ClickableCard
      tabIndex={0}
      data-test="recent-campaign-alert"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-clickable={hoverable}
      {...rest}
    >
      <FlexSpacer
        flexDirection="column"
        alignItems="flex-start"
        gap={spacing.half}
        flexGrow={1}
        flexShrink={1}
      >
        <span>
          <ResourceName>{resourceName}</ResourceName> <AlertText>{content}</AlertText>
        </span>
        <Caption>{caption}</Caption>
      </FlexSpacer>

      <FlexSpacer alignItems="center" justifyContent="center">
        <ChevronRightNew16 color={colors.iconPrimary} />
      </FlexSpacer>
    </ClickableCard>
  );
};
